import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link as LinkEditor } from '@tiptap/extension-link';
import { Plugin, TextSelection } from 'prosemirror-state';
import { EmbedBlock } from '../components/embed-block';
import { Review } from '@/utils/api/reviews/reviews.data-model';

export function Editor({ review }: { review?: Review }) {
  const editor = useEditor({
    immediatelyRender: false,
    editable: false,
    extensions: [
      StarterKit,
      LinkEditor.extend({
        addProseMirrorPlugins() {
          const editor = this.editor;
          return [
            new Plugin({
              view() {
                const dom = editor.view.dom;

                const handleClick = (event: MouseEvent) => {
                  const target = event.target as HTMLElement;
                  const link = target.closest('a[href]');
                  if (!link) return;

                  event.preventDefault();
                  event.stopImmediatePropagation();
                  event.stopPropagation();

                  const href = (link as HTMLAnchorElement).href;
                  const rect = link.getBoundingClientRect();
                  const text = link.textContent ?? '';

                  const pos = editor.view.posAtDOM(link, 0);
                  if (pos == null) return;

                  const { state, view } = editor;
                  const resolved = state.doc.resolve(pos);

                  if (
                    resolved.nodeAfter?.type.name === 'embed' ||
                    resolved.nodeBefore?.type.name === 'embed'
                  ) {
                    return;
                  }

                  const nodeSize = text.length ?? 0;
                  const from = pos;
                  const to = pos + nodeSize;

                  const docSize = state.doc.content.size;
                  if (from < 0 || to > docSize || from > to) return;

                  if (resolved.parent.type.spec.atom) return;

                  const tr = state.tr.setSelection(
                    TextSelection.create(state.doc, from, to)
                  );
                  view.dispatch(tr);

                  window.dispatchEvent(
                    new CustomEvent('tiptap-link-click', {
                      detail: { href, rect, text, from, to },
                    })
                  );
                };

                dom.addEventListener('mousedown', handleClick, true);
                return {
                  destroy() {
                    dom.removeEventListener('mousedown', handleClick, true);
                  },
                };
              },
            }),
          ];
        },
      }).configure({
        openOnClick: false,
        autolink: true,
        enableClickSelection: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
      }),
      EmbedBlock,
    ],
    content: review?.description ?? null,
  });

  if (!editor) return null;

  return <EditorContent editor={editor} />;
}
